import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useHookFormMask } from "use-mask-input";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";

import "./profile.css";
import Button from "../../../components/button/button";
import Badge from "../../../components/badge/badge";
import KeyIcon from "../../../assets/icons/key.svg";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import ErrorMessage from "../../../components/forms/errorMessage/errorMessage";
import CheckedCircle from "../../../assets/icons/checked-circle.svg";
import { PHONE_REGEX } from "../../../utils/pagesMapper";
import { useUser } from "../../../context/UserContext";
import { getCookie } from "../../../utils/cookies";
import { readUserById, updateUser } from "../../../services/academetrics";

const Profile = () => {
  const { user, setUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(!user);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  const registerWithMask = useHookFormMask(register);

  // Busca dados do usuário apenas se não tiver no contexto e estiver logado
  useEffect(() => {
    const fetchUserIfNeeded = async () => {
      if (user) {
        setIsLoadingData(false);
        return;
      }

      const token = getCookie("token");
      if (!token) {
        setIsLoadingData(false);
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const userId = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] 
          || decoded["sub"] 
          || decoded["userId"]
          || decoded["nameid"];

        if (userId) {
          const userData = await readUserById(userId);
          if (userData) {
            setUser(userData);
          }
        }
      } catch (error) {
        console.log("Erro ao buscar dados do usuário:", error);
        toast.error("Erro ao carregar dados do perfil");
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchUserIfNeeded();
  }, [user, setUser]);

  // Carrega os dados do formulário quando o usuário estiver disponível
  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber
      });
    }
  }, [user, reset]);

  const getRoleLabel = (role) => {
    const roleMap = {
      'Admin': 'Administrador',
      'Manager': 'Gerente',
      'Academic': 'Acadêmico',
      'AcademicPlus': 'Acadêmico Plus',
      'Financial': 'Financeiro',
      'FinancialPlus': 'Financeiro Plus',
      'Marketing': 'Marketing',
      'Operator': 'Operador'
    };
    return roleMap[role] || role;
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      await updateUser({ ...data, userId: user?.id });
      toast.success("Perfil atualizado com sucesso!");
      
      // Atualiza os dados no contexto
      setUser({ ...user, ...data });
    } catch (error) {
      console.log("@error", error);
      toast.error("Erro ao atualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingData || !user) {
    return (
      <div>
        <HeaderTitle title="Meu Perfil" />
        <div className="mainContentSpace">
          <div className="profile-loading">
            <span>Carregando dados do perfil...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderTitle title="Meu Perfil" />

      <div className="mainContentSpace">
        <div className="profile-container">
          {/* Card de informações do usuário */}
          <div className="profile-info-card">
            <div className="profile-avatar">
              <span className="avatar-initials">
                {user?.firstName?.[0]?.toUpperCase() || "U"}
                {user?.lastName?.[0]?.toUpperCase() || ""}
              </span>
            </div>
            <div className="profile-details">
              <h2 className="profile-name">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="profile-email">{user?.email}</p>
              <Badge text={getRoleLabel(user?.role)} color="blue" />
            </div>
          </div>

          {/* Formulário de edição */}
          <div className="profile-form-container">
            <form onSubmit={handleSubmit(onSubmit)} className="profile-form">
              <fieldset>
                <legend>Informações Pessoais</legend>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">Nome</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="Nome"
                      {...register("firstName", {
                        required: "O nome é obrigatório."
                      })}
                    />
                    <ErrorMessage message={errors.firstName?.message} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Sobrenome</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="Sobrenome"
                      {...register("lastName", {
                        required: "O sobrenome é obrigatório."
                      })}
                    />
                    <ErrorMessage message={errors.lastName?.message} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phoneNumber">Telefone</label>
                    <input
                      type="text"
                      id="phoneNumber"
                      name="phoneNumber"
                      placeholder="Telefone"
                      {...registerWithMask("phoneNumber", ["(99) 99999-9999"], {
                        required: "O telefone é obrigatório.",
                        pattern: {
                          value: PHONE_REGEX,
                          message: "O telefone é inválido."
                        }
                      })}
                    />
                    <ErrorMessage message={errors.phoneNumber?.message} />
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend>Acesso</legend>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">E-mail</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="E-mail"
                      {...register("email", {
                        required: "O e-mail é obrigatório.",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "O e-mail é inválido."
                        }
                      })}
                    />
                    <ErrorMessage message={errors.email?.message} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Nova Senha (opcional)</label>
                    <div className="password-input-container">
                      <img src={KeyIcon} alt="Senha" className="password-icon" />
                      <input
                        type="password"
                        id="password"
                        name="password"
                        placeholder="Digite para alterar a senha"
                        {...register("password", {
                          minLength: {
                            value: 6,
                            message: "A senha deve ter no mínimo 6 caracteres."
                          }
                        })}
                      />
                    </div>
                    <ErrorMessage message={errors.password?.message} />
                  </div>
                </div>
              </fieldset>

              <div className="form-actions">
                <Button
                  text="Salvar Alterações"
                  variation="primary"
                  size="big"
                  icon={CheckedCircle}
                  loading={loading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
